import React, { useState } from "react";
import FormInput from "../../../props/propertyFormProp";
import { postData } from "../../../fetchRoutes/postData";

export default function AuctionFormPopup({ property, onClose, onAuctionSuccess }) {

    const addAuction = process.env.REACT_APP_GET_AN_AUCTION;

    const [formData, setFormData] = useState({
        startTime: "",
        endTime: "",
        increment: "",
        reservePrice: ""
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [showCalendar, setShowCalendar] = useState({
        startTime: false,
        endTime: false
    });

    // Handle form field changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Automatically adjust min date for end time
        if (name === "startTime") {
            setFormData((prev) => ({
                ...prev,
                endTime: value > prev.endTime ? "" : prev.endTime
            }));
        }

        // Clear validation errors in real-time
        validateField(name, value);
    };
    
    const validateField = (name, value) => {
        const errors = { ...fieldErrors };

        // Date validation
        if (name === "startTime") {
            const now = new Date();
            const start = new Date(value);
            if (start <= now) {
                errors.startTime = "Start time must be in the future.";
            } else {
                delete errors.startTime;  // Clear error
            }
        }

        if (name === "endTime") {
            const start = new Date(formData.startTime);
            const end = new Date(value);
            if (end <= start) {
                errors.endTime = "End time must be after the start time.";
            } else {
                delete errors.endTime;  // Clear error
            }
        }

        // Increment validation
        if (name === "increment") {
            const increment = parseInt(value, 10);
            if (isNaN(increment) || increment <= 0) {
                errors.increment = "Increment must be greater than 0.";
            } else if (increment % 1000 !== 0) {
                errors.increment = "Increment must be in thousands.";
            } else {
                delete errors.increment;  // Clear error
            }
        }

        // Reserve price validation
        if (name === "reservePrice") {
            const reservePrice = parseInt(value, 10);
            if (isNaN(reservePrice) || reservePrice <= 0) {
                errors.reservePrice = "Reserve price must be greater than 0.";
            } else if (reservePrice % 1000 !== 0) {
                errors.reservePrice = "Reserve price must be in thousands.";
            } else {
                delete errors.reservePrice;  // Clear error
            }
        }

        setFieldErrors(errors);
    };


    // Form submission

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
    
        try {
            const url = `${addAuction}/${property._id}/addAuction`;
            const response = await postData(url, formData);

            if (response.status === "Success") {
                onClose();
                onAuctionSuccess(property._id);
                setIsLoading(false);
            } else {
                setError(response.message || "Auction creation failed.");
                setIsLoading(false);
            }
        } catch (error) {
            setError(error.message || "An error occurred.");
            setIsLoading(false);
        }
    };

    // Get min date for datetime-local input (disable past dates)
    const getMinDateTime = () => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);  // Start of the day
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset()); 
        return now.toISOString().slice(0, 16);
    };

    // Toggle Calendar
    const toggleCalendar = (field) => {
        setShowCalendar((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    return (
        <div className="popupOverlay">
            {isLoading && (
                <div className="loaderContainer">
                    <div className="loader"></div>
                    <p className="loadingText"> Adding your property to auction...</p>
                </div>
            )}
            <div className="popupForm">
                <h2 className="popUpHeading">{property?.title}</h2>

                <form onSubmit={handleFormSubmit} className="auctionForm">

                    {/* Start Time */}
                  
                    <FormInput
                        label="Start Time:"
                        name="startTime"
                        type="datetime-local"
                        value={formData.startTime}
                        onChange={handleChange}
                        min={getMinDateTime()}  // Disable past dates
                        required
                        className={fieldErrors.startTime ? "error" : ""}
                        errorMessage={fieldErrors.startTime} 
                    />
            
                    {showCalendar.startTime && (
                        <div className="calendar-popup">
                            <button onClick={() => toggleCalendar("startTime")}>Done</button>
                            <button onClick={() => toggleCalendar("startTime")}>X</button>
                        </div>
                    )}
                
                 
                    {fieldErrors.startTime && <span className="error">{fieldErrors.startTime}</span>}

                    {/* End Time */}
                  
                    <FormInput
                        label="End Time:"
                        name="endTime"
                        type="datetime-local"
                        value={formData.endTime}
                        onChange={handleChange}
                        min={formData.startTime || getMinDateTime()}  // Disable dates before start time
                        required
                        className={fieldErrors.endTime ? "error" : ""}
                        errorMessage={fieldErrors.endTime} 
                    />
           
                    {showCalendar.endTime && (
                        <div className="calendar-popup">
                            <button onClick={() => toggleCalendar("endTime")}>Done</button>
                            <button onClick={() => toggleCalendar("endTime")}>X</button>
                        </div>
                    )}
                 
             
                    {/* Increment */}
                    <FormInput
                        label="Increment"
                        name="increment"
                        type="number"
                        placeholder="2000"
                        value={formData.increment}
                        onChange={handleChange}
                        required
                        className={fieldErrors.increment ? "error" : ""}
                        errorMessage={fieldErrors.increment} 
                    />

                    {/* Reserve Price */}
                    <FormInput
                        label="Reserve Price"
                        name="reservePrice"
                        type="number"
                        placeholder="2000"
                        value={formData.reservePrice}
                        onChange={handleChange}
                        required
                        className={fieldErrors.reservePrice ? "error" : ""}
                        errorMessage={fieldErrors.reservePrice} 
                    />

                    <div className="formButtons">
                        <button type="button" onClick={onClose} className="cancelAuction">Cancel</button>
                        <button type="submit" className="submitAuction">Submit</button>
                        
                    </div>

                </form>

                {error && (
                    <div className="errorMessage">
                        <span className="error">{error}</span>
                    </div>
                )}
            </div>
        </div>
    );
};
